import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import type { AgentId, Message, MessageKind } from '@shared/types';
import { api, ApiError } from '../lib/api';
import { Layout } from '../components/Layout';
import { Markdown } from '../components/Markdown';
import { Orb } from '../components/Orb';
import { parseReceipts } from '../lib/feedReceipts';
import { useAssetUrl } from '../lib/useAssetUrl';

const AGENTS: AgentId[] = ['emma', 'milo', 'leo', 'quinn', 'iris', 'nora', 'grace'];

/** Kind → chip class + label. Partial by design: any kind not mapped here (a new
 *  MessageKind added later — e.g. Work Board thread entries) falls back to a neutral
 *  chip labelled with its own kind, so the feed never breaks or mislabels it. */
const KIND: Partial<Record<MessageKind, { cls: string; label: string }>> = {
  chat: { cls: 'chip-ice', label: 'chat' },
  task: { cls: 'chip-ice live', label: 'task' },
  signal: { cls: 'chip-violet', label: 'signal' },
  incident: { cls: 'chip-coral', label: 'incident' },
  result: { cls: 'chip-mint', label: 'result' },
  knowledge: { cls: 'chip-amber', label: 'knowledge' },
  standup: { cls: 'chip-dim', label: 'standup' },
  correction: { cls: 'chip-amber', label: 'correction' },
  question: { cls: 'chip-ice', label: 'question' },
  approval: { cls: 'chip-violet', label: 'approval' },
};

function isAgent(id: string): id is AgentId {
  return (AGENTS as string[]).includes(id);
}

function cap(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

function whoName(m: Message): string {
  if (m.author_type === 'system') return 'System';
  return cap(m.author_id);
}

function when(iso: string): string {
  const d = new Date(iso);
  const t = d.getTime();
  if (Number.isNaN(t)) return '';
  const diff = (Date.now() - t) / 1000;
  if (diff < 45) return 'now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** One evidence screenshot the agent captured, shown as a tappable thumbnail
 *  that opens full-size — the Dashboard's "trust the receipts" pattern, reused
 *  on the Feed. Silent if the key won't resolve or the image 404s. */
function ReceiptThumb({ keyRef }: { keyRef: string }) {
  const url = useAssetUrl(keyRef);
  const [broken, setBroken] = useState(false);
  if (!url || broken) return null;
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      title="Open the full screenshot"
      style={{ display: 'inline-block', textDecoration: 'none' }}
    >
      <img
        src={url}
        alt="Screenshot the agent captured as proof"
        loading="lazy"
        onError={() => setBroken(true)}
        style={{ display: 'block', maxWidth: 148, maxHeight: 96, borderRadius: 7, border: '1px solid var(--line)' }}
      />
    </a>
  );
}

/** The receipts under a feed message: a plain fact line and/or tappable
 *  screenshots. Internal UUIDs and raw JSON are stripped by parseReceipts, so a
 *  founder only ever sees English and proof they can open. */
function Receipts({ refs }: { refs: unknown }) {
  const { images, facts } = useMemo(() => parseReceipts(refs), [refs]);
  if (!facts && images.length === 0) return null;
  return (
    <>
      {facts && <div className="receipt">{facts}</div>}
      {images.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
          {images.map((k) => (
            <ReceiptThumb key={k} keyRef={k} />
          ))}
          <span className="receipt" style={{ color: 'var(--ice)', alignSelf: 'flex-end' }}>
            📎 {images.length === 1 ? 'the receipt — tap to open ↗' : `${images.length} screenshots — tap to open ↗`}
          </span>
        </div>
      )}
    </>
  );
}

/** A rendered feed row: either one message, or a collapsed run of ≥3 consecutive
 *  same-morning standups (they're 7–9/day and drown out results/incidents/questions). */
type FeedRow = { type: 'single'; m: Message } | { type: 'standups'; id: string; items: Message[] };

/** Fold consecutive top-level `standup` messages into one group when there are ≥3 in a
 *  row; everything else stays a single row. Input is newest-first (as loaded). Pure. */
export function groupFeed(list: Message[]): FeedRow[] {
  const rows: FeedRow[] = [];
  let run: Message[] = [];
  const flush = () => {
    if (run.length >= 3) {
      rows.push({ type: 'standups', id: run[0].id, items: run });
    } else {
      for (const m of run) rows.push({ type: 'single', m });
    }
    run = [];
  };
  for (const m of list) {
    if (m.kind === 'standup' && (m.depth ?? 0) === 0) {
      run.push(m);
    } else {
      flush();
      rows.push({ type: 'single', m });
    }
  }
  flush();
  return rows;
}

function Avatar({ m }: { m: Message }) {
  if (m.author_type === 'agent' && isAgent(m.author_id)) {
    return <Orb agent={m.author_id} size="xs" />;
  }
  const founder = m.author_type === 'founder';
  const initial = (m.author_id || (founder ? 'F' : 'S')).charAt(0).toUpperCase();
  return (
    <span
      aria-hidden
      className="mono"
      style={{
        width: 20,
        height: 20,
        flexShrink: 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 10,
        fontWeight: 600,
        borderRadius: '50%',
        color: founder ? 'var(--ice)' : 'var(--dim)',
        background: founder ? 'rgba(var(--ice-rgb),.14)' : 'rgba(var(--ice-rgb),.06)',
        border: '1px solid var(--line-2)',
      }}
    >
      {initial}
    </span>
  );
}

/** One message row (used both flat and inside an expanded standup group). */
function FeedItem({ m, onReply }: { m: Message; onReply: (m: Message) => void }) {
  const cfg = KIND[m.kind] ?? { cls: 'chip-dim', label: m.kind };
  return (
    <div className="feed-item">
      <Avatar m={m} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span className="who">{whoName(m)}</span>
          <span className={`chip ${cfg.cls}`}>
            <i />
            {cfg.label}
          </span>
          <span className="when">{when(m.created_at)}</span>
          <button
            className="btn btn-ghost sm"
            style={{ marginLeft: 'auto', padding: '2px 8px', fontSize: 11.5 }}
            onClick={() => onReply(m)}
          >
            Reply
          </button>
        </div>
        <Markdown text={m.body_md} mentions style={{ marginTop: 4, fontSize: 13.5, color: 'var(--dim)' }} />
        <Receipts refs={m.refs} />
      </div>
    </div>
  );
}

/** A collapsed run of same-morning standups. Collapsed by default so the daily wall of
 *  7–9 near-identical cards doesn't bury the feed's real signal; taps open to the details. */
function StandupGroup({ items, onReply }: { items: Message[]; onReply: (m: Message) => void }) {
  const [open, setOpen] = useState(false);
  // Unique agent orbs, in feed order, for an at-a-glance "who checked in" cluster.
  const agents = useMemo(() => {
    const seen = new Set<string>();
    const out: AgentId[] = [];
    for (const m of items) {
      if (m.author_type === 'agent' && isAgent(m.author_id) && !seen.has(m.author_id)) {
        seen.add(m.author_id);
        out.push(m.author_id);
      }
    }
    return out;
  }, [items]);

  return (
    <div className="feed-item" style={{ flexDirection: 'column', gap: 0, alignItems: 'stretch' }}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
          background: 'none',
          border: 'none',
          padding: 0,
          margin: 0,
          cursor: 'pointer',
          textAlign: 'left',
          color: 'inherit',
          font: 'inherit',
          width: '100%',
        }}
      >
        <span className="chip chip-dim">
          <i />
          standups
        </span>
        <span className="who">{items.length} daily check-ins</span>
        {agents.length > 0 && (
          <span style={{ display: 'inline-flex', gap: 3 }}>
            {agents.map((a) => (
              <Orb key={a} agent={a} size="xs" />
            ))}
          </span>
        )}
        <span className="when">{when(items[0].created_at)}</span>
        <span className="btn btn-ghost sm" style={{ marginLeft: 'auto', padding: '2px 8px', fontSize: 11.5 }}>
          {open ? 'Hide' : 'Show'}
        </span>
      </button>
      {open && (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {items.map((m) => (
            <FeedItem key={m.id} m={m} onReply={onReply} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function Feed() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [text, setText] = useState('');
  const [posting, setPosting] = useState(false);
  const [postError, setPostError] = useState<string | null>(null);
  const [focused, setFocused] = useState(false);
  const [replyTo, setReplyTo] = useState<Message | null>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);

  const rows = useMemo(() => groupFeed(messages), [messages]);

  const load = useCallback(async () => {
    try {
      const data = await api.get<Message[]>('/feed');
      const list = Array.isArray(data) ? data : [];
      list.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
      setMessages(list);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? `Couldn't load the feed (HTTP ${e.status}).` : "Couldn't reach the feed.");
    } finally {
      setLoading(false);
    }
  }, []);

  // Poll GET /feed every 5s (per 03 realtime spec); load once immediately.
  useEffect(() => {
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [load]);

  async function post() {
    const body = text.trim();
    if (!body || posting) return;
    setPosting(true);
    setPostError(null);
    try {
      const payload: { body_md: string; kind: string; thread_id?: string } = { body_md: body, kind: 'chat' };
      if (replyTo) payload.thread_id = replyTo.thread_id;
      await api.post('/feed/messages', payload);
      setText('');
      setReplyTo(null);
      await load();
    } catch (e) {
      setPostError(e instanceof ApiError ? `Post failed (HTTP ${e.status}).` : 'Post failed — try again.');
    } finally {
      setPosting(false);
    }
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      void post();
    }
  }

  /** Put the composer into "replying in thread" mode and bring it into view. */
  function startReply(m: Message) {
    setReplyTo(m);
    composerRef.current?.focus({ preventScroll: true });
    composerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  return (
    <Layout title="Feed">
      {/* Composer */}
      <div className="card" style={{ marginBottom: 18 }}>
        {replyTo && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 10,
              marginBottom: 10,
              padding: '7px 12px',
              background: 'rgba(var(--ice-rgb),.06)',
              border: '1px solid var(--line-2)',
              borderRadius: 'var(--r-ctl)',
            }}
          >
            <span className="small">
              Replying to{' '}
              <span style={{ color: 'var(--ice)', fontWeight: 600 }}>{whoName(replyTo)}</span> in thread
            </span>
            <button
              className="btn btn-ghost sm"
              aria-label="Cancel reply"
              style={{ padding: '2px 8px' }}
              onClick={() => setReplyTo(null)}
            >
              ✕
            </button>
          </div>
        )}
        <textarea
          ref={composerRef}
          aria-label={replyTo ? `Reply to ${whoName(replyTo)}'s thread` : 'Post a message to the office'}
          placeholder={
            replyTo ? 'Write your reply…' : 'Share an update, or @mention an agent to hand off a task…'
          }
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={{
            width: '100%',
            minHeight: 92,
            resize: 'vertical',
            background: 'var(--panel-2)',
            color: 'var(--text)',
            border: `1px solid ${focused ? 'var(--line-2)' : 'var(--line)'}`,
            borderRadius: 'var(--r-ctl)',
            padding: '12px 14px',
            fontFamily: 'var(--font)',
            fontSize: 14,
            lineHeight: 1.6,
            outline: 'none',
            transition: 'border-color .16s',
          }}
        />
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            flexWrap: 'wrap',
            marginTop: 12,
          }}
        >
          <span className="small mono">
            @emma @milo @leo @quinn @iris @nora @grace · @all
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {postError && <span className="small" style={{ color: 'var(--coral)' }}>{postError}</span>}
            <button className="btn btn-primary" onClick={() => void post()} disabled={!text.trim() || posting}>
              {posting ? 'Posting…' : replyTo ? 'Reply' : 'Post'}
            </button>
          </div>
        </div>
      </div>

      {/* Stream */}
      {loading ? (
        <p className="small">Loading the office…</p>
      ) : error && messages.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '40px 20px' }}>
          <p className="dim">{error}</p>
          <button className="btn btn-line sm" style={{ marginTop: 14 }} onClick={() => void load()}>
            Retry
          </button>
        </div>
      ) : messages.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '48px 20px' }}>
          <Orb agent="emma" size="md" />
          <p className="dim" style={{ marginTop: 16 }}>
            The office is quiet. Post the first message — @mention an agent to put them to work.
          </p>
        </div>
      ) : (
        <div>
          {rows.map((r) =>
            r.type === 'standups' ? (
              <StandupGroup key={r.id} items={r.items} onReply={startReply} />
            ) : (
              <FeedItem key={r.m.id} m={r.m} onReply={startReply} />
            ),
          )}
        </div>
      )}
    </Layout>
  );
}
